import { DateMode, MatrixColumnType, type FormField, type MatrixColumn, type MatrixTableRow } from './form';
import { readMatrixCell } from './matrixAnswer';

/**
 * Rules across the cells of one matrix row, set per matrix in the form builder under `options.rowRules` (ENG-9023).
 * A rule says whether a row's answer is valid, never whether it is answered: a row holding nothing a rule reads is
 * left to the Required check (ENG-9018 decision 5). Cells are read through `readMatrixCell`, never off the wire string.
 */

/** The numbers in `columnIds` add up to `total` in every row that holds one of them. */
export interface MatrixRowSumRule {
  kind: 'rowSum';
  columnIds: number[];
  total: number;
}

/** The date in `endColumnId` is later than the one in `startColumnId` in every row that holds both. */
export interface MatrixDateOrderRule {
  kind: 'dateOrder';
  startColumnId: number;
  endColumnId: number;
}

export type MatrixRowRule = MatrixRowSumRule | MatrixDateOrderRule;

/**
 * A row that breaks a rule, with what its message names: the row, by its header or, for a row with none, by its place
 * counted from 1, and the total or the two columns.
 */
export type MatrixRuleViolation =
  | { kind: 'rowSum'; row: string; position: number; total: number; sum: number }
  | { kind: 'dateOrder'; row: string; position: number; start: string; end: string };

/** The row a violation names. */
type RowName = Pick<MatrixRuleViolation, 'row' | 'position'>;

/** A label on one line: the builder's one-line box takes a typed `\n` for a break, as the matrix header draws it. */
const oneLine = (label: string) => label.split(/\\n|\n/).map(part => part.trim()).filter(Boolean).join(' ');

const sumViolation = (rule: MatrixRowSumRule, row: MatrixTableRow, name: RowName): MatrixRuleViolation | undefined => {
  let sum = 0;
  let read = false;
  for (const columnId of rule.columnIds) {
    const value = readMatrixCell(row, columnId);
    if (typeof value === 'number' && Number.isFinite(value)) {
      sum += value;
      read = true;
    }
  }
  // Rounded to nine places, so decimals that add up to the total pass and the message gives the sum a reader expects.
  const rounded = Math.round(sum * 1e9) / 1e9;
  return !read || rounded === rule.total ? undefined : { kind: 'rowSum', ...name, total: rule.total, sum: rounded };
};

const readDate = (row: MatrixTableRow, columnId: number) => {
  const value = readMatrixCell(row, columnId);
  return value instanceof Date && !Number.isNaN(value.getTime()) ? value : undefined;
};

const dateOrderViolation = (
  rule: MatrixDateOrderRule,
  row: MatrixTableRow,
  name: RowName,
  columnLabel: (columnId: number) => string
): MatrixRuleViolation | undefined => {
  const start = readDate(row, rule.startColumnId);
  const end = readDate(row, rule.endColumnId);
  if (!start || !end || end.getTime() > start.getTime()) {
    return undefined;
  }
  return { kind: 'dateOrder', ...name, start: columnLabel(rule.startColumnId), end: columnLabel(rule.endColumnId) };
};

/** The first row, top to bottom, that breaks one of the matrix's rules, or `undefined` when every row keeps them. */
export const matrixRuleViolation = (field: FormField): MatrixRuleViolation | undefined => {
  const rules = field.options.rowRules ?? [];
  const rows = field.matrixTableRowValues ?? [];
  const columnLabel = (columnId: number) =>
    oneLine(field.options.columns?.find(column => column.id === columnId)?.label ?? '');
  for (const [index, row] of rows.entries()) {
    // A row a DynamicRows condition copied without its header is named by its place.
    const name = { row: oneLine(row.rowHeader ?? ''), position: index + 1 };
    for (const rule of rules) {
      const violation = rule.kind === 'rowSum' ?
        sumViolation(rule, row, name) :
        dateOrderViolation(rule, row, name, columnLabel);
      if (violation) {
        return violation;
      }
    }
  }
  return undefined;
};

/** A column a sum can read: a number column that holds one number, not a range's `"from-to"`. */
export const isSummableColumn = (column: Pick<MatrixColumn, 'type' | 'options'>) =>
  column.type === MatrixColumnType.NUMBER && !column.options?.range;

/**
 * What a date column's cell holds, for telling whether two can be compared: a date (UTC midnight), a local date and
 * time, or an absolute one (UTC). A time of day has none, since an end time past midnight reads as earlier than its
 * start. Two columns of different kinds decode to instants that mean different things, so no order between them holds.
 */
export const dateKind = (column: Pick<MatrixColumn, 'type' | 'options'>): string | undefined => {
  if (column.type !== MatrixColumnType.DATE || column.options?.mode === DateMode.TIME) {
    return undefined;
  }
  if (column.options?.mode === DateMode.DATE_TIME) {
    return column.options.absoluteDate ? 'datetime-absolute' : 'datetime-local';
  }
  return 'date';
};

/**
 * The rules the builder saves: each sum keeps only the columns that are still plain number columns and goes when fewer
 * than two are left or it has no total, and a date order goes unless it names two different date columns of one
 * `dateKind`. A column deleted or switched to another type after its rule was set would otherwise leave a rule nothing
 * can satisfy, or one comparing values that mean different things.
 */
export const keptRowRules = (rules: MatrixRowRule[], columns: Pick<MatrixColumn, 'id' | 'type' | 'options'>[]) => {
  const column = (id: number | undefined) => columns.find(candidate => candidate.id === id);
  return rules.flatMap((rule): MatrixRowRule[] => {
    if (rule.kind === 'rowSum') {
      const columnIds = rule.columnIds.filter(id => {
        const found = column(id);
        return !!found && isSummableColumn(found);
      });
      return columnIds.length >= 2 && Number.isFinite(rule.total) ? [{ ...rule, columnIds }] : [];
    }
    const start = column(rule.startColumnId);
    const end = column(rule.endColumnId);
    return start && end && start !== end && dateKind(start) !== undefined && dateKind(start) === dateKind(end) ?
      [rule] :
      [];
  });
};
